import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import Input from '../Input';
import CaptchaInput from '../CaptchaInput';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';
import { CloseOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { checkSensitiveWords } from '../../utils/sensitiveWords';
import Message from '../Message';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding-top: 80px;

  @media (max-width: 768px) {
    padding-top: 140px;
    align-items: flex-start;
    overflow-y: auto;
  }
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 16px;
  padding: 32px 24px;
  width: 100%;
  max-width: 447px;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 16px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 0 16px;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 32px;
  right: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  height: 32px;
  transition: all 0.3s ease;
  z-index: 100;
  
  &:hover {
    opacity: 0.6;
  }
`;

const Title = styled.h2`
  text-align: left;
  color: ${({ theme }) => theme.text};
  font-size: 20px;
  font-weight: 500;
  height: 32px;
  line-height: 32px;
  margin: 0;
`;

const StepTitle = styled.h3`
  text-align: left;
  color: ${({ theme }) => theme.text};
  font-size: 20px;
  font-weight: 500;
  height: 32px;
  line-height: 32px;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 32px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled(Input)`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: #E8E8E8;
  padding: 0 16px;
  box-sizing: border-box;
  
  input {
    height: 44px;
    background-color: #E8E8E8 !important;
    color: #000000;
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
    border: none;
    transition: all 0.3s ease;
    outline: none;
    line-height: 44px;
    padding: 0;
    
    &::placeholder {
      color: #888888;
    }

    &:hover {
      background-color: #E8E8E8 !important;
    }

    &:focus {
      background-color: #E8E8E8 !important;
      outline: none;
    }

    /* 处理浏览器自动填充的背景色 - 强化版 */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
      box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
      -webkit-text-fill-color: #000000 !important;
      caret-color: #000000;
      transition: background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s;
      background-color: #E8E8E8 !important;
    }
  }

  &:focus-within {
    border: 1px solid #FF4D4F;
    background: #E8E8E8 !important;
    outline: none;
    
    input {
      background-color: #E8E8E8 !important;
      outline: none;
    }
  }

  /* 移除所有蓝色阴影 */
  &,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-affix-wrapper-focused {
    box-shadow: none !important;
    outline: none !important;
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }

  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:hover,
  .ant-input:focus,
  .ant-input:hover {
    box-shadow: none !important;
    outline: none !important;
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }
  
  /* 添加更强的CSS规则覆盖Chrome的自动填充样式 */
  @-webkit-keyframes autofill {
    to {
      color: #000000;
      background: #E8E8E8;
    }
  }
  
  input:-webkit-autofill {
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }
  
  /* 额外的CSS规则来防止浏览器自动填充样式 */
  input:-internal-autofill-selected {
    background-color: #E8E8E8 !important;
    background-image: none !important;
    color: #000000 !important;
  }
  
  /* 通用样式覆盖 */
  @-webkit-keyframes onAutoFillStart { from {} to {}}
  @keyframes onAutoFillStart { from {} to {}}
  
  input:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-fill-mode: both;
  }
  
  input:autofill {
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }

  /* 额外覆盖Ant Design内部input元素 */
  .ant-input-affix-wrapper > input.ant-input {
    color: #000000 !important;
    background-color: #E8E8E8 !important;
  }
  
  .ant-input-affix-wrapper .ant-input-suffix {
    color: #000000 !important;
  }
`;

const PasswordInput = styled(StyledInput)`
  .ant-input-suffix {
    color: #000000;
    cursor: pointer;
    margin-right: 12px;
    font-size: 18px;
    display: flex;
    align-items: center;
    
    &:hover {
      color: rgba(0, 0, 0, 0.65);
    }

    .anticon {
      color: #000000;
      
      &:hover {
        color: rgba(0, 0, 0, 0.65);
      }
    }
  }
`;

const CaptchaWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
`;

const CaptchaInputWrapper = styled.div`
  flex: 1;
`;

const CaptchaDisplay = styled.div`
  width: 100px;
  height: 44px;
  border-radius: 22px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.inputBg || theme.cardBg};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.border};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    will-change: transform;
    transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-font-smoothing: subpixel-antialiased;
    image-rendering: -webkit-optimize-contrast;
  }

  div {
    color: ${({ theme }) => theme.text};
  }
`;

const VerificationCode = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  justify-content: center;
  position: relative;
  
  /* 移动端优化 */
  @media (max-width: 768px) {
    gap: 8px;
    padding: 0 16px;
  }
`;

const CodeInput = styled(StyledInput)`
  flex: 1;
  min-width: 0;
`;

const SendButton = styled.button`
  height: 48px;
  padding: 0 24px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(90deg, #FF4D4F, #FF7875);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, #E53E3E, #FF4D4F);
  }
  
  &:disabled {
    background: #EBEBEB;
    color: #999;
    cursor: not-allowed;
  }
`;

const NextButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: #FF4D4F;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 24px;
  position: relative;
  z-index: 10;
  
  &:hover {
    background: #E53E3E;
  }
  
  &:disabled {
    background: #FFB2B3;
    cursor: not-allowed;
  }
`;

const LoginLink = styled.a`
  color: #FF4D4F;
  font-size: 14px;
  text-align: center;
  margin-top: 16px;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    color: #E53E3E;
  }
`;

const InputError = styled.div`
  color: #FF4D4F;
  font-size: 12px;
  margin-top: 4px;
  padding-left: 12px;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const PhoneNumber = styled.div`
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  text-align: center;
  margin-bottom: 8px;
`;

const CountdownText = styled.div`
  color: ${({ theme }) => theme.secondaryText};
  font-size: 14px;
  text-align: center;
  margin-bottom: 24px;
`;

const VerificationCodeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 24px 0;
  width: 100%;
`;

const VerificationCodeBox = styled.div`
  width: 61.57223892211914px;
  height: 72px;
  position: relative;
  border-radius: 15px;
  border: 1px solid ${({ theme, $active, $focused }) =>
    $focused ? '#FF4D4F' :
    $active ? '#FF4D4F' : theme.border};
  background: ${({ theme, $focused }) =>
    $focused ? 'rgba(255, 77, 79, 0.05)' :
    theme.inputBg || theme.cardBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${({ theme }) => theme.text};
  transition: all 0.3s ease;
  box-shadow: ${({ $focused }) =>
    $focused ? '0 0 0 2px rgba(255, 77, 79, 0.2)' : 'none'};
  
  /* 防止字体加载引起的布局抖动 */
  min-width: 52px;
  max-width: 61.57223892211914px;
  min-height: 60px;
  max-height: 72px;
  
  /* 使用系统字体作为后备 */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'HanaMinA', sans-serif;
  
  /* 强制使用硬件加速 */
  transform: translateZ(0);
  backface-visibility: hidden;
  
  @media (max-width: 768px) {
    width: 52px;
    height: 60px;
    font-size: 16px;
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  cursor: text;
  pointer-events: none;
  
  /* 移动端优化 */
  @media (max-width: 768px) {
    font-size: 16px;
    -webkit-text-size-adjust: 100%;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 77, 79, 0.3);
  border-radius: 50%;
  border-top-color: #FF4D4F;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ResendButton = styled.div`
  color: #FF4D4F;
  font-size: 14px;
  text-align: center;
  margin-top: 16px;
  cursor: pointer;
  
  &:hover {
    color: #E53E3E;
    text-decoration: underline;
  }
  
  &:disabled {
    color: ${({ theme }) => theme.secondaryText};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const TopInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`;

const PhoneText = styled.span`
  color: ${({ theme }) => theme.text};
  font-size: 14px;
`;

const ResendText = styled.span`
  color: #FF4D4F;
  font-size: 14px;
  cursor: pointer;
  z-index: 10;
  
  &:hover {
    color: #E53E3E;
    text-decoration: underline;
  }
  
  &:disabled {
    color: ${({ theme }) => theme.secondaryText};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const RegisterModal = ({ onClose, onShowLogin }) => {
  const { login } = useAuth();
  const { isDarkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    captcha: '',
    smsCode: '',
    loginName: '',
    password: '',
    userName: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaImage, setCaptchaImage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const smsInputRef = React.useRef(null);
  const [rid, setRid] = useState('');

  const focusSmsInput = () => {
    if (smsInputRef.current) {
      smsInputRef.current.focus();
      // 在移动端，延迟设置焦点位置，确保键盘弹出后再设置
      setTimeout(() => {
        setFocusedIndex(formData.smsCode.length);
      }, 100);
    }
  };

  const fetchCaptcha = async () => {
    try {
      setIsLoadingCaptcha(true);
      // 生成随机数作为rid
      const randomId = Math.floor(Math.random() * 1000000000).toString();
      setRid(randomId);
      
      const timestamp = new Date().getTime(); // 添加时间戳防止缓存
      const response = await axios.get(`//server.battleverse.cn/pub/captcha.do?t=${timestamp}&rid=${randomId}`);
      if (response.data.success) {
        // 预加载图片
        const img = document.createElement('img');
        img.onload = () => {
          setCaptchaImage(`data:image/jpeg;base64,${response.data.data.captcha}`);
          setIsLoadingCaptcha(false);
        };
        img.onerror = () => {
          Message.error('验证码加载失败，请点击刷新', 'register-modal-message');
          setIsLoadingCaptcha(false);
        };
        img.src = `data:image/jpeg;base64,${response.data.data.captcha}`;
      } else {
        Message.error('获取验证码失败，请刷新重试', 'register-modal-message');
        setIsLoadingCaptcha(false);
      }
    } catch (err) {
      Message.error('获取验证码失败，请刷新重试', 'register-modal-message');
      setIsLoadingCaptcha(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const initCaptcha = async () => {
      try {
        setIsLoadingCaptcha(true);
        // 生成随机数作为rid
        const randomId = Math.floor(Math.random() * 1000000000).toString();
        setRid(randomId);
        
        const timestamp = new Date().getTime(); // 添加时间戳防止缓存
        const response = await axios.get(`//server.battleverse.cn/pub/captcha.do?t=${timestamp}&rid=${randomId}`);
        if (mounted && response.data.success) {
          // 预加载图片
          const img = document.createElement('img');
          img.onload = () => {
            if (mounted) {
              setCaptchaImage(`data:image/jpeg;base64,${response.data.data.captcha}`);
              setIsLoadingCaptcha(false);
            }
          };
          img.onerror = () => {
            if (mounted) {
              Message.error('验证码加载失败，请点击刷新', 'register-modal-message');
              setIsLoadingCaptcha(false);
            }
          };
          img.src = `data:image/jpeg;base64,${response.data.data.captcha}`;
        } else if (mounted) {
          Message.error('获取验证码失败，请刷新重试', 'register-modal-message');
          setIsLoadingCaptcha(false);
        }
      } catch (err) {
        if (mounted) {
          Message.error('获取验证码失败，请刷新重试', 'register-modal-message');
          setIsLoadingCaptcha(false);
        }
      }
    };

    initCaptcha();
    
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (step === 2 && smsInputRef.current) {
      setTimeout(() => {
        focusSmsInput();
      }, 100);
    }
  }, [step]);

  useEffect(() => {
    // 监听字体加载完成事件
    const handleFontsLoaded = () => {
      if (document.fonts.ready) {
        // 重新计算布局
        window.dispatchEvent(new Event('resize'));
      }
    };

    // 如果字体已经加载完成
    if (document.fonts.ready) {
      handleFontsLoaded();
    } else {
      // 等待字体加载完成
      document.fonts.ready.then(handleFontsLoaded);
    }

    return () => {
      // 清理代码
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 对用户名进行敏感词检测
    if (name === 'userName') {
      const hasSensitiveWords = checkSensitiveWords(value).length > 0;
      if (hasSensitiveWords) {
        Message.error('检测到敏感词，请重新输入', 'register-modal-message');
        return; // 不更新状态，保持输入框为空
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'smsCode') {
      setFocusedIndex(value.length);
    }
    
    setError('');
  };

  const handleSmsCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setFormData(prev => ({
      ...prev,
      smsCode: value
    }));
    setFocusedIndex(value.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && formData.smsCode.length > 0) {
      // 删除最后一个数字
      setFormData(prev => ({
        ...prev,
        smsCode: prev.smsCode.slice(0, -1)
      }));
      setFocusedIndex(formData.smsCode.length - 1);
    }
  };

  const handleSendSmsCode = async () => {
    if (!formData.phoneNumber.match(/^1\d{10}$/)) {
      Message.error('请输入正确的手机号码', 'register-modal-message');
      return false;
    }

    try {
      const response = await axios.post('//server.battleverse.cn/pub/send-msg.do', {
        phoneNumber: formData.phoneNumber,
        captcha: formData.captcha,
        rid: rid
      });

      if (response.data.success) {
        setCountdown(60);
        return true;
      } else {
        Message.error(response.data.msg, 'register-modal-message');
        // 清空验证码输入框
        setFormData(prev => ({
          ...prev,
          captcha: ''
        }));
        fetchCaptcha();
        return false;
      }
    } catch (err) {
      Message.error(err.response?.data?.msg || '系统异常，请稍后重试', 'register-modal-message');
      // 清空验证码输入框
      setFormData(prev => ({
        ...prev,
        captcha: ''
      }));
      fetchCaptcha();
      return false;
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // 提交前再次检查用户名敏感词
    if (checkSensitiveWords(formData.userName).length > 0) {
      Message.error('检测到敏感词，请重新输入', 'register-modal-message');
      setFormData(prev => ({
        ...prev,
        userName: ''
      }));
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post('//server.battleverse.cn/pub/register.do', {
        ...formData,
        rid: rid
      });
      console.log('注册响应:', response.data);

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        // 存储token和用户信息到本地存储
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // 显示成功提示
        Message.success(response.data.msg || '注册成功', 'register-modal-message');
        
        // 关闭模态框
        onClose();
        
        // 延迟一下刷新页面，让用户看到成功消息
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        console.error('注册失败:', response.data.msg);
        Message.error(response.data.msg, 'register-modal-message');
      }
    } catch (err) {
      console.error('注册请求错误:', err);
      Message.error(err.response?.data?.msg || '系统异常，请稍后重试', 'register-modal-message');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      if (step === 1) {
        // 验证手机号格式
        if (!formData.phoneNumber.match(/^1\d{10}$/)) {
          Message.error('请输入正确的手机号码', 'register-modal-message');
          return;
        }
        // 验证图形验证码不为空
        if (!formData.captcha) {
          Message.error('请输入图形验证码', 'register-modal-message');
          return;
        }
        
        // 如果rid为空，则生成一个随机数作为rid
        if (!rid) {
          const randomId = Math.floor(Math.random() * 1000000000).toString();
          setRid(randomId);
        }
        
        // 发送短信验证码并等待结果
        const success = await handleSendSmsCode();
        if (success) {
          setStep(2);
        }
      } else if (step === 2) {
        if (!formData.smsCode) {
          Message.error('请输入验证码', 'register-modal-message');
          return;
        }
        
        if (formData.smsCode.length !== 4) {
          Message.error('请输入4位验证码', 'register-modal-message');
          return;
        }

        // 验证短信验证码
        try {
          const response = await axios.post('//server.battleverse.cn/pub/verify-msg-code.do', {
            phoneNumber: formData.phoneNumber,
            smsCode: formData.smsCode,
            rid: rid
          });

          if (response.data.success) {
            // 设置loginName并跳转到第三步
            setFormData(prevData => ({
              ...prevData,
              loginName: prevData.phoneNumber
            }));
            
            // 确保状态更新后再跳转
            setTimeout(() => {
              setStep(3);
            }, 0);
          } else {
            Message.error(response.data.msg, 'register-modal-message');
            // 清空验证码输入框
            setFormData(prev => ({
              ...prev,
              smsCode: ''
            }));
          }
        } catch (err) {
          console.error('验证短信验证码失败:', err);
          Message.error(err.response?.data?.msg || '系统异常，请稍后重试', 'register-modal-message');
          // 清空验证码输入框
          setFormData(prev => ({
            ...prev,
            smsCode: ''
          }));
        }
      }
    } catch (err) {
      console.error('处理下一步时出错:', err);
      Message.error('操作失败，请重试', 'register-modal-message');
    }
  };

  const handleSwitchToLogin = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
    if (typeof onShowLogin === 'function') {
      onShowLogin();
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <Form onSubmit={(e) => e.preventDefault()}>
            {formData.phoneNumber && countdown > 0 && (
              <PhoneNumber style={{ marginBottom: '16px', color: '#FF4D4F' }}>
                请重新输入图形验证码以发送短信
              </PhoneNumber>
            )}
            <InputGroup>
              <StyledInput
                type="tel"
                name="phoneNumber"
                placeholder="请输入手机号"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
              />
            </InputGroup>
            <InputGroup>
              <CaptchaWrapper>
                <CaptchaInputWrapper>
                  <StyledInput
                    type="text"
                    name="captcha"
                    placeholder="请输入验证码"
                    value={formData.captcha}
                    onChange={handleInputChange}
                    required
                  />
                </CaptchaInputWrapper>
                <CaptchaDisplay onClick={isLoadingCaptcha ? undefined : fetchCaptcha} style={{ pointerEvents: isLoadingCaptcha ? 'none' : 'auto' }}>
                  {isLoadingCaptcha ? (
                    <LoadingSpinner />
                  ) : captchaImage ? (
                    <img src={captchaImage} alt="验证码" />
                  ) : (
                    <div>点击加载</div>
                  )}
                </CaptchaDisplay>
              </CaptchaWrapper>
            </InputGroup>
            <NextButton type="button" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNext();
            }} disabled={!formData.phoneNumber || !formData.captcha}>
              下一步
            </NextButton>
            <LoginLink onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSwitchToLogin();
            }}>登录账号</LoginLink>
          </Form>
        );
      case 2:
        return (
          <Form onSubmit={(e) => {
            e.preventDefault();
            // 删除这里可能会触发的表单提交转跳逻辑
          }}>
            <StepTitle>输入验证码</StepTitle>
            <TopInfoContainer>
              <PhoneText>验证码已发送至 {formData.phoneNumber}</PhoneText>
              {countdown > 0 ? (
                <ResendText as="div" disabled>
                  重新发送 ({countdown}s)
                </ResendText>
              ) : (
                <ResendText
                  as="div"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      // 跳回第一步，让用户重新输入图形验证码
                      setStep(1);
                      // 清空验证码输入
                      setFormData(prev => ({
                        ...prev,
                        captcha: '',
                        smsCode: ''
                      }));
                      // 重新获取图形验证码
                      fetchCaptcha();
                    } catch (err) {
                      console.error('处理重新发送失败:', err);
                      Message.error('操作失败，请稍后重试', 'register-modal-message');
                    }
                  }}
                >
                  重新发送
                </ResendText>
              )}
            </TopInfoContainer>
            <VerificationCode>
              <HiddenInput
                ref={smsInputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={formData.smsCode}
                onChange={handleSmsCodeChange}
                onFocus={() => setFocusedIndex(formData.smsCode.length)}
                onBlur={() => setFocusedIndex(-1)}
                onKeyDown={handleKeyDown}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  focusSmsInput();
                }}
              />
              {Array.from({ length: 4 }).map((_, index) => (
                <VerificationCodeBox
                  key={index}
                  $active={index < formData.smsCode.length}
                  $focused={index === focusedIndex}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    focusSmsInput();
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    focusSmsInput();
                  }}
                >
                  {formData.smsCode[index] || ''}
                </VerificationCodeBox>
              ))}
            </VerificationCode>
            <NextButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNext();
              }}
              disabled={formData.smsCode.length !== 4}
              style={{ cursor: 'pointer', pointerEvents: 'auto', position: 'relative', zIndex: 20 }}
            >
              下一步
            </NextButton>
          </Form>
        );
      case 3:
        return (
          <Form onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}>
            <StepTitle>输入信息</StepTitle>
            <StyledInput
              type="text"
              name="loginName"
              placeholder="账号"
              value={formData.phoneNumber}
              disabled={true}
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <StyledInput
              type="text"
              name="userName"
              placeholder="请输入昵称"
              value={formData.userName}
              onChange={handleInputChange}
              required
            />
            <PasswordInput
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="请输入密码（不少于6位）"
              value={formData.password}
              onChange={handleInputChange}
              required
              suffix={
                showPassword ?
                  <EyeOutlined onClick={(e) => {
                    e.stopPropagation();
                    setShowPassword(false);
                  }} /> :
                  <EyeInvisibleOutlined onClick={(e) => {
                    e.stopPropagation();
                    setShowPassword(true);
                  }} />
              }
            />
            <NextButton
              type="submit"
              disabled={loading || !formData.password || !formData.userName}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {loading ? '注册中...' : '完成注册'}
            </NextButton>
          </Form>
        );
      default:
        return null;
    }
  };

  return (
    <ModalOverlay onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}>
      <div id="register-modal-message" style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
        width: 'calc(100% - 32px)',
        padding: '0 16px',
        boxSizing: 'border-box'
      }} />
      <ModalContent onClick={(e) => {
        e.stopPropagation();
        if (step === 2) {
          focusSmsInput();
        }
      }}>
        <CloseButton onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}>
          <CloseOutlined />
        </CloseButton>
        {step === 1 && <Title>注册</Title>}
        {renderStepContent()}
      </ModalContent>
    </ModalOverlay>
  );
};

export default RegisterModal;
